/**
 * QuantityBucketBar — net-new shared component (holistic plan §Net-new, S4.3).
 *
 * The Quantity Buckets 數量分段 visual: one track scaled to the ordered
 * quantity with overlaid confirmed / loaded / received bars; the unfilled
 * remainder of the track IS the derived Order Balance 待出量.
 *
 * Accessibility contract (ux-lens finding 6):
 * - every segment carries its NUMBER as a non-colour channel — the numeric
 *   strip is always visible, never tooltip-only;
 * - a negative balance (over-load) renders as the signed numeric PLUS a
 *   warning glyph — never colour alone;
 * - numerics are width-bounded (tabular-nums + wrap) for both grid-cell and
 *   drawer widths.
 *
 * Namespace-agnostic by design (ReasonRequiredModal precedent): all
 * user-facing strings arrive via `labels` so each consumer localizes from
 * its own i18n namespace.
 */

import { Tooltip, Typography, theme } from 'antd';
import { WarningOutlined } from '@ant-design/icons';

const { Text } = Typography;

export interface QuantityBucketBarLabels {
  ordered: string;
  confirmed: string;
  loaded: string;
  received: string;
  /** Order Balance 待出量 label. */
  balance: string;
  /** Tooltip/aria text for the negative-balance (over-load) warning glyph. */
  negativeWarning: string;
}

export interface QuantityBucketBarProps {
  qtyOrdered: number;
  qtyConfirmed: number;
  qtyLoaded: number;
  qtyReceived: number;
  /** Signed Order Balance — derived by the caller (never computed/stored here). */
  orderBalance: number;
  labels: QuantityBucketBarLabels;
  /** 'cell' = width-bounded grid cell (default) · 'drawer' = full width. */
  size?: 'cell' | 'drawer';
}

interface SegmentStat {
  key: string;
  label: string;
  value: number;
  color?: string;
}

/**
 * Signed numeric with the mandatory warning glyph when negative — exported so
 * grid cells (待出量 columns) can reuse the exact same non-colour rendering.
 */
export const BalanceValue = ({
  value,
  warningTitle,
  strong = false,
}: {
  value: number;
  warningTitle: string;
  strong?: boolean;
}) => {
  const { token } = theme.useToken();
  const negative = value < 0;
  return (
    <span
      style={{
        fontVariantNumeric: 'tabular-nums',
        fontWeight: strong ? token.fontWeightStrong : undefined,
        color: negative ? token.colorError : undefined,
        whiteSpace: 'nowrap',
      }}
    >
      {negative && (
        <Tooltip title={warningTitle}>
          <WarningOutlined
            aria-label={warningTitle}
            style={{ color: token.colorError, marginRight: token.marginXXS }}
          />
        </Tooltip>
      )}
      {value}
    </span>
  );
};

const QuantityBucketBar = ({
  qtyOrdered,
  qtyConfirmed,
  qtyLoaded,
  qtyReceived,
  orderBalance,
  labels,
  size = 'cell',
}: QuantityBucketBarProps) => {
  const { token } = theme.useToken();

  // Scale to the largest bucket so an over-load still renders in-bounds —
  // the signed numbers below stay the truth channel.
  const scale = Math.max(qtyOrdered, qtyConfirmed, qtyLoaded, qtyReceived, 1);
  const pct = (value: number) => `${Math.min(100, Math.max(0, (value / scale) * 100))}%`;

  const isCell = size === 'cell';
  const trackHeight = isCell ? 8 : 12;

  const overlays: Array<{ key: string; value: number; color: string }> = [
    { key: 'confirmed', value: qtyConfirmed, color: token.colorPrimaryBorder },
    { key: 'loaded', value: qtyLoaded, color: token.colorPrimary },
    { key: 'received', value: qtyReceived, color: token.colorSuccess },
  ];

  const stats: SegmentStat[] = [
    { key: 'ordered', label: labels.ordered, value: qtyOrdered },
    { key: 'confirmed', label: labels.confirmed, value: qtyConfirmed },
    { key: 'loaded', label: labels.loaded, value: qtyLoaded },
    { key: 'received', label: labels.received, value: qtyReceived },
  ];

  return (
    <div
      style={{
        width: '100%',
        minWidth: isCell ? 170 : undefined,
        maxWidth: isCell ? 260 : undefined,
      }}
    >
      {/* Track = ordered quantity; the unfilled remainder IS 待出量. */}
      <div
        style={{
          position: 'relative',
          height: trackHeight,
          borderRadius: trackHeight / 2,
          backgroundColor: token.colorFillSecondary,
          overflow: 'hidden',
        }}
      >
        {overlays.map((overlay) => (
          <div
            key={overlay.key}
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: pct(overlay.value),
              borderRadius: trackHeight / 2,
              backgroundColor: overlay.color,
            }}
          />
        ))}
      </div>

      {/* Numeric strip — the always-visible non-colour channel. */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          columnGap: token.marginSM,
          rowGap: 0,
          marginTop: token.marginXXS,
        }}
      >
        {stats.map((stat) => (
          <Text
            key={stat.key}
            style={{
              fontSize: token.fontSizeSM,
              color: token.colorTextSecondary,
              whiteSpace: 'nowrap',
            }}
          >
            {stat.label}{' '}
            <span
              style={{
                color: token.colorText,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {stat.value}
            </span>
          </Text>
        ))}
        <Text
          style={{
            fontSize: token.fontSizeSM,
            color: token.colorTextSecondary,
            whiteSpace: 'nowrap',
          }}
        >
          {labels.balance}{' '}
          <BalanceValue value={orderBalance} warningTitle={labels.negativeWarning} />
        </Text>
      </div>
    </div>
  );
};

export default QuantityBucketBar;
